import React from 'react';
import { Auth } from 'aws-amplify';
import { Box, Drawer, Typography } from '@mui/material';
import { SiteInfo } from 'shared/components';
import { useToggle } from 'shared/hooks';
import { UserPopoverHq } from 'shared/components/user-popover/user-popover';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { StyledSidebar } from 'shared/components/sidebar/styled-sidebar';
import { StyledSidebarLogo } from 'shared/components/sidebar/styled-sidebar-logo';
import { SidebarSectionTitle } from 'shared/components/sidebar/sidebar-section-title';
import { SidebarSection } from 'shared/components/sidebar/sidebar-section';
import { SidebarSectionList } from 'shared/components/sidebar/sidebar-section-list';
import { SidebarSectionLink } from 'shared/components/sidebar/sidebar-section-link';
import { SidebarProfileSection } from 'shared/components/sidebar/sidebar-profile-section';
import { SidebarToggle } from 'shared/components/sidebar/sidebar-toggle';
import { SiteInfoContainer } from 'shared/components/sidebar/site-info-container';
import { useSidebarProvider } from 'shared/contexts/sidebar-provider';
import { useGetHqNotifications } from './hooks/use-get-hq-notifications';
import { useSidebarItems } from './sidebar-items';
import { getWebappBrandName, SMALL_LOGO_PATH, WEBAPP_BRAND, WIDE_LOGO_PATH } from '../../../config';
import { AuthenticatedElement } from '../common/authenticated-element';
import { useHqRelativePath } from '../../hooks/useHqRelativePath';
import { invariant } from '../../../shared/utils/utils';
import { useGetUserInfo } from '../../hooks/queryHooks/users/useGetUserInfo';

export const Sidebar = () => {
  const { isSidebarOpen, handleClose, handleOpen } = useSidebarProvider();
  const [userLabel, setUserLabel] = React.useState('');
  const { data } = useGetHqNotifications();
  const userQuery = useGetUserInfo();
  const generatePath = useHqRelativePath();
  const { on: userInfoOpen, toggle: toggleUserInfo } = useToggle();
  const queryClient = useQueryClient();
  const items = useSidebarItems();

  const { t } = useTranslation('hq-screen-specific');

  const logout = async () => {
    await Auth.signOut();
    queryClient.removeQueries();
  };

  React.useEffect(() => {
    let isMounted = true;
    Auth.currentUserInfo().then((res) => {
      if (isMounted && res) {
        setUserLabel(res.attributes?.email ?? '');
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const getSectionPermissions = (section: (typeof items)[number]['key']) => {
    const group = items.find((i) => i.key === section);
    invariant(group);

    return {
      roles: group.children.map((child) => child.roles).flat(),
      policyGroup: group.children[0].policyGroup,
    };
  };

  return (
    <>
      <StyledSidebar variant="permanent" folded={!isSidebarOpen}>
        <StyledSidebarLogo folded={!isSidebarOpen} to={generatePath(items[0].children[0])}>
          <img
            src={isSidebarOpen ? WIDE_LOGO_PATH : SMALL_LOGO_PATH}
            alt={getWebappBrandName(WEBAPP_BRAND)}
          />
        </StyledSidebarLogo>

        <Box px={2} textOverflow="ellipsis" textAlign="center">
          <Typography variant="body1">{t('sidebar.title')}</Typography>
        </Box>

        {items.map((sidebarItem) => {
          const permissions = getSectionPermissions(sidebarItem.key);

          return (
            // @ts-expect-error This fails on typing because it's shared between OGP and HQ. No clue how to fix this now
            <AuthenticatedElement {...permissions} key={sidebarItem.key}>
              <SidebarSection>
                <SidebarSectionTitle isSidebarOpen={isSidebarOpen}>
                  {sidebarItem.name}
                </SidebarSectionTitle>
                <SidebarSectionList>
                  {sidebarItem.children.map((child) => {
                    const notificationCount = data?.[child.key] ?? 0;
                    const path = generatePath(child);
                    return (
                      <AuthenticatedElement
                        key={child.key}
                        roles={child.roles}
                        policyGroup={child.policyGroup}
                      >
                        <SidebarSectionLink
                          {...child}
                          path={path}
                          notificationCount={notificationCount}
                          isSidebarOpen={isSidebarOpen}
                        />
                      </AuthenticatedElement>
                    );
                  })}
                </SidebarSectionList>
              </SidebarSection>
            </AuthenticatedElement>
          );
        })}
        <SidebarProfileSection
          isSidebarOpen={isSidebarOpen}
          handleToggleUserInfo={toggleUserInfo}
          handleLogout={logout}
        />
        <SiteInfoContainer isSidebarOpen={isSidebarOpen}>
          <SiteInfo />
        </SiteInfoContainer>
      </StyledSidebar>
      <SidebarToggle
        isSidebarOpen={isSidebarOpen}
        handleClose={handleClose}
        handleOpen={handleOpen}
      />
      <Drawer anchor="right" open={userInfoOpen} onClose={toggleUserInfo}>
        <UserPopoverHq
          userLabel={userLabel}
          userRoles={
            userQuery.data?.roles.hqRoles
              ? {
                  companyRoles: userQuery.data?.roles.hqRoles,
                  branchOfficesWithDepartmentRoles: [],
                }
              : undefined
          }
        ></UserPopoverHq>
      </Drawer>
    </>
  );
};
